import { CommonModule } from '@angular/common';
import {
    booleanAttribute,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** A generic color tone the lib ships chip and badge styles for. */
export type TokenTone = 'blue' | 'green' | 'violet' | 'amber' | 'neutral';

/** An optional per-token badge: a short label and a generic color tone the lib ships styles for. */
export interface TokenBadge {
    label: string;
    tone?: TokenTone;
}

/**
 * House searchable multi-select combo: a text input that shows selected values as removable tokens and
 * offers a themed suggestion list as you type. Replaces PrimeNG `p-autoComplete` so the dropdown matches
 * the light app (the vendor widget rendered its panel with a dark theme). The value is a string[]; the
 * parent supplies `suggestions` in response to the `search` output (debounced query text).
 *
 * Two layout modes: the default single-line control (sized for a filter toolbar), and `wrap` - a
 * taller field whose tokens wrap onto multiple lines, for forms. Tokens can carry an optional kind
 * badge (`badgeFor`) and be flagged invalid per token (`validateToken`).
 */
@Component({
    selector: 'app-token-combo',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './token-combo.component.html',
    styleUrls: ['./token-combo.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TokenComboComponent),
            multi: true,
        },
    ],
})
export class TokenComboComponent implements ControlValueAccessor, OnChanges {
    @Input() placeholder = '';
    @Input() suggestions: string[] = [];
    /** Debounce before emitting `search`. 0 for local lists, ~200 for an API-backed typeahead. */
    @Input() searchDelay = 200;
    /** Parent-driven busy state (e.g. a server re-query the committed tokens triggered). */
    @Input() loading = false;
    /** Free-text mode: commit whatever is typed as a token (Enter, comma, or blur), not only picks from
     *  the suggestion list. Used for the IP/CIDR and port filters, which have no suggestions.
     *  `transform` so a bare `allowCustom` attribute (no value) reads as true. */
    @Input({ transform: booleanAttribute }) allowCustom = false;
    /** Hide the dropdown chevron (for free-text filters with no suggestions). */
    @Input() showToggle = true;
    /** Paint the control as invalid (red border) - the parent validates the committed tokens. */
    @Input() invalid = false;
    /** Wrap tokens onto multiple lines in a taller, form-sized field (vs. the single-line toolbar look). */
    // `transform` so a bare `wrap` attribute (no value) reads as true, the way <app-token-combo wrap> is written.
    @Input({ transform: booleanAttribute }) wrap = false;
    /** Optional per-token badge (e.g. an address-kind tag). Returns null for no badge. */
    @Input() badgeFor?: (token: string) => TokenBadge | null;
    /** Optional per-token validity. A token for which this returns false is marked invalid. */
    @Input() validateToken?: (token: string) => boolean;

    /** With `validateToken`, refuse to commit a token that fails it rather than adding it as an invalid
     *  chip: the typed text stays in the box and the control shows invalid, so a bad value is never added. */
    @Input({ transform: booleanAttribute }) rejectInvalid = false;
    /** Optional per-token color tone - tints the whole chip (and its matching dropdown option), e.g.
     *  green for a named identity vs blue for a role. Returns null for the default neutral chip. */
    @Input() toneFor?: (token: string) => TokenTone | null;
    /** Optional rewrite applied to every value as it's committed (typed, pasted, or picked), e.g. to
     *  prefix a bare role with '#'. Picked suggestions are already final, so it must be idempotent. */
    @Input() normalizeToken?: (raw: string) => string;
    /** Narrow the supplied `suggestions` by the typed text in the control itself, instead of relying on
     *  the parent to return an already-filtered list. For a list that is local (or only partly server). */
    @Input({ transform: booleanAttribute }) filterSuggestionsLocally = false;
    /** Lay the dropdown out as wrapping tinted pills that match the selected chips (vs. a plain list),
     *  the way the identity attribute picker reads. */
    @Input({ transform: booleanAttribute }) chipOptions = false;
    /** Keep already-chosen options in the dropdown (marked as selected) instead of removing them, so the
     *  list stays stable and reads as a checklist. Picking a chosen option then toggles it back off. */
    @Input({ transform: booleanAttribute }) keepChosenVisible = false;
    /** Cap how many selected chips the field shows before collapsing the rest behind a "+N more" control,
     *  so a large selection does not blow the field up. Unset (the default) shows every chip. */
    @Input() maxVisibleChips?: number;
    /** Runtime state: the operator expanded a capped field to show every chip. */
    chipsExpanded = false;
    /** Open the suggestion panel upward when there is more room above the field than below. */
    panelUp = false;
    /** Cap the panel to the room actually available in the viewport, so it never spills off-screen. */
    panelMaxHeightPx: number | null = null;
    /** Optional grouped suggestions: render the dropdown as labeled sections (e.g. Destination / Source)
     *  instead of one flat list. When set, it supersedes `suggestions` as the option pool - the items are
     *  flattened in group order for local filtering and keyboard navigation, so a section header is never
     *  itself selectable or a stop on the arrow keys. Empty sections drop out. */
    @Input() suggestionGroups?: { label: string; items: string[] }[];
    /** Disable the control: tokens still show but can't be added or removed, and the field can't be
     *  focused or typed into. Kept mounted (rather than hidden by the parent) so a field that doesn't
     *  apply in the current mode stays in place, just inert. */
    @Input({ transform: booleanAttribute }) disabled = false;
    /** When disabled, an optional reason shown as the control's tooltip. */
    @Input() disabledReason?: string;

    @Output() search = new EventEmitter<string>();

    @ViewChild('field', { static: true }) field!: ElementRef<HTMLInputElement>;
    @ViewChild('root', { static: true }) root!: ElementRef<HTMLElement>;

    value: string[] = [];
    query = '';
    open = false;
    focused = false;
    activeIndex = -1;
    /** The current typed entry was rejected as invalid (rejectInvalid mode), so paint the control invalid
     *  until the next keystroke. */
    entryInvalid = false;
    /** A suggestion request is in flight (combo mode) - drives the in-control spinner. */
    searching = false;

    private searchTimer: any;
    private onChange: (value: string[]) => void = () => {};
    private onTouched: () => void = () => {};

    /** Show the spinner while the parent re-queries or a typeahead request is pending. */
    get busy(): boolean {
        return this.loading || this.searching;
    }

    /** The badge for a token, or null when none is configured. */
    badgeOf(token: string): TokenBadge | null {
        return this.badgeFor ? this.badgeFor(token) ?? null : null;
    }

    /** The color tone for a token, or null for the default neutral chip. */
    toneOf(token: string): TokenTone | null {
        return this.toneFor ? this.toneFor(token) ?? null : null;
    }

    /** True when a token fails the parent's per-token validity check. */
    isTokenInvalid(token: string): boolean {
        return !!this.validateToken && !this.validateToken(token);
    }

    ngOnChanges(changes: SimpleChanges): void {
        // New suggestions arrived - the typeahead request is done.
        if (changes['suggestions']) {
            this.searching = false;
        }
    }

    writeValue(value: any): void {
        this.value = Array.isArray(value) ? [...value] : [];
    }
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    /** The option pool in render order: the flattened groups when grouped, else the flat `suggestions`. */
    private get suggestionPool(): string[] {
        return this.suggestionGroups?.length ? this.suggestionGroups.flatMap((g) => g.items) : this.suggestions || [];
    }

    /** True when an option should be shown: not already chosen, and matching the typed text when the list
     *  is filtered locally. Shared by the flat list and the grouped sections so their order lines up. */
    private isOptionVisible(option: string, chosen: Set<string>, q: string): boolean {
        return (this.keepChosenVisible || !chosen.has(option)) && (!q || option.toLowerCase().includes(q));
    }

    /** The selected chips to render: all of them, or just the first `maxVisibleChips` when capped and
     *  collapsed. The slice keeps each chip at its real index in `value`, so remove() still works. */
    get visibleTokens(): string[] {
        return this.maxVisibleChips != null && !this.chipsExpanded && this.value.length > this.maxVisibleChips
            ? this.value.slice(0, this.maxVisibleChips)
            : this.value;
    }
    /** How many chips are hidden by the cap right now (0 when uncapped or expanded). */
    get hiddenTokenCount(): number {
        return this.maxVisibleChips != null && !this.chipsExpanded
            ? Math.max(0, this.value.length - this.maxVisibleChips)
            : 0;
    }
    toggleChips(event: Event): void {
        event.stopPropagation();
        this.chipsExpanded = !this.chipsExpanded;
    }

    /** Whether an option is already one of the selected tokens (used to mark it in keep-visible mode). */
    isChosen(option: string): boolean {
        const token = this.normalizeToken ? this.normalizeToken(option) : option;
        return this.value.includes(token);
    }

    /** The local-filter query, or '' when the parent returns an already-filtered list. */
    private get localQuery(): string {
        return this.filterSuggestionsLocally ? this.query.trim().toLowerCase() : '';
    }

    /** Suggestions minus the ones already chosen, optionally narrowed by the typed text. In grouped mode
     *  this is the sections flattened in order, so it stays the single source for keyboard nav. */
    get visibleSuggestions(): string[] {
        const chosen = new Set(this.value);
        const q = this.localQuery;
        return this.suggestionPool.filter((s) => this.isOptionVisible(s, chosen, q));
    }

    /** The dropdown split into labeled sections (grouped mode only). Each kept option carries its index in
     *  `visibleSuggestions` so the active-row highlight and arrow keys line up across sections; empty
     *  sections (every option chosen or filtered out) are dropped. */
    get visibleGroups(): { label: string; options: { value: string; index: number }[] }[] {
        if (!this.suggestionGroups?.length) {
            return [];
        }
        const chosen = new Set(this.value);
        const q = this.localQuery;
        const out: { label: string; options: { value: string; index: number }[] }[] = [];
        let index = 0;
        for (const group of this.suggestionGroups) {
            const options: { value: string; index: number }[] = [];
            for (const item of group.items) {
                if (this.isOptionVisible(item, chosen, q)) {
                    options.push({ value: item, index: index++ });
                }
            }
            if (options.length) {
                out.push({ label: group.label, options });
            }
        }
        return out;
    }

    /** In free-text mode, the value the current query would create - or null if it's blank, already
     *  chosen, or an exact match of a suggestion. Lets the panel offer a "create" affordance while typing
     *  so the list never just disappears when nothing matches. */
    get createCandidate(): string | null {
        if (!this.allowCustom) return null;
        const raw = this.query.trim();
        if (!raw) return null;
        const token = (this.normalizeToken ? this.normalizeToken(raw) : raw).trim();
        if (!token || this.value.includes(token)) return null;
        const lower = token.toLowerCase();
        return this.visibleSuggestions.some((s) => s.toLowerCase() === lower) ? null : token;
    }

    focusField(): void {
        if (this.disabled) return;
        this.field?.nativeElement?.focus();
    }

    /** Clicking/tabbing into a combo opens its list right away (loads the first page of suggestions);
     *  free-text filters have no list, so they just focus. */
    onFocus(): void {
        this.focused = true;
        if (this.showToggle) {
            this.openPanel();
            this.debouncedSearch(this.query);
        }
    }

    onInput(text: string): void {
        this.query = text;
        // Typing clears the rejected-entry mark; the next commit re-checks it.
        this.entryInvalid = false;
        this.openPanel();
        this.debouncedSearch(text);
    }

    togglePanel(): void {
        if (this.disabled) return;
        if (this.open) {
            this.closePanel();
            return;
        }
        this.openPanel();
        this.debouncedSearch(this.query);
        this.focusField();
    }

    private openPanel(): void {
        this.open = true;
        this.activeIndex = -1;
        this.positionPanel();
    }

    /** Decide which way the panel opens and how tall it may be, from the room above/below the control in
     *  the viewport - so a field near the bottom of the page flips up (or at least caps its height) rather
     *  than spilling off-screen. Measured from the control, which is already laid out when the panel opens. */
    private positionPanel(): void {
        if (typeof window === 'undefined') return;
        const box = this.root?.nativeElement?.querySelector('.tc-box') as HTMLElement | null;
        if (!box) return;
        const rect = box.getBoundingClientRect();
        const margin = 8;
        const cap = 240; // the panel's default max height (~15rem)
        const below = window.innerHeight - rect.bottom - margin;
        const above = rect.top - margin;
        // Flip up only when below can't hold a usable panel and there is genuinely more room above.
        this.panelUp = below < Math.min(cap, 160) && above > below;
        const avail = this.panelUp ? above : below;
        this.panelMaxHeightPx = Math.max(120, Math.min(cap, Math.floor(avail)));
    }
    private closePanel(): void {
        this.open = false;
        this.activeIndex = -1;
    }

    private debouncedSearch(query: string): void {
        clearTimeout(this.searchTimer);
        // Only combos run a search; show the spinner until fresh suggestions land (ngOnChanges clears it).
        // Local filtering is instant and triggers no suggestions round-trip, so it shows no spinner.
        if (this.showToggle && !this.filterSuggestionsLocally) {
            this.searching = true;
        }
        this.searchTimer = setTimeout(() => this.search.emit(query ?? ''), this.searchDelay);
    }

    selectSuggestion(suggestion: string): void {
        // In keep-visible mode the list is a checklist, so picking an already-chosen option removes it.
        if (this.keepChosenVisible && this.isChosen(suggestion)) {
            const token = this.normalizeToken ? this.normalizeToken(suggestion) : suggestion;
            this.remove(this.value.indexOf(token));
        } else {
            this.addValue(suggestion);
        }
        this.query = '';
        if (this.field) this.field.nativeElement.value = '';
        this.activeIndex = -1;
        this.focusField();
        // Keep the panel open for multi-pick and refresh the list.
        this.debouncedSearch('');
    }

    /** Commit the "create" affordance (the whole query as one new token), keeping the panel open. */
    createFromQuery(): void {
        const token = this.createCandidate;
        if (!token) return;
        // Same rule as a typed commit: a value that fails validation is held in the box, marked invalid.
        if (this.rejectInvalid && this.isTokenInvalid(this.normalizedToken(token))) {
            this.entryInvalid = true;
            return;
        }
        this.addValue(token);
        this.query = '';
        if (this.field) this.field.nativeElement.value = '';
        this.activeIndex = -1;
        this.focusField();
        if (this.showToggle) this.debouncedSearch('');
    }

    /** Trim then apply the optional normalizer, giving the token as it would be committed. */
    private normalizedToken(raw: string): string {
        const token = (raw ?? '').trim();
        return this.normalizeToken && token ? this.normalizeToken(token) : token;
    }

    private addValue(raw: string): void {
        const token = this.normalizedToken(raw);
        if (!token || this.value.includes(token)) return;
        // In rejectInvalid mode a value that fails validation is never added (the caller keeps it in the
        // input instead); without it, an invalid token is still added and merely marked.
        if (this.rejectInvalid && this.isTokenInvalid(token)) return;
        this.value = [...this.value, token];
        this.onChange([...this.value]);
    }

    /** Commit the typed text as one or more tokens (free-text mode). Splits on commas/whitespace so a
     *  pasted "10.0.0.1, 10.0.0.2" becomes two tokens, matching the previous chips input. */
    private commitCustom(): void {
        const parts = (this.query || '').split(/[,\s]+/g).filter(Boolean);
        let added = false;
        const rejected: string[] = [];
        for (const part of parts) {
            // In rejectInvalid mode, hold back a value that fails validation (keep it typed so it can be
            // fixed) rather than committing it as a bad chip.
            if (this.rejectInvalid && this.isTokenInvalid(this.normalizedToken(part))) {
                rejected.push(part);
                continue;
            }
            const before = this.value.length;
            this.addValue(part);
            if (this.value.length > before) added = true;
        }
        if (rejected.length) {
            // Leave the rejected text in the box, marked invalid, so the user sees what to correct; keep the
            // panel open. Any valid parts in the same entry were still committed above.
            this.query = rejected.join(' ');
            if (this.field) this.field.nativeElement.value = this.query;
            this.entryInvalid = true;
            this.activeIndex = -1;
            return;
        }
        this.query = '';
        if (this.field) this.field.nativeElement.value = '';
        this.activeIndex = -1;
        if (added) this.closePanel();
    }

    onPaste(event: ClipboardEvent): void {
        if (!this.allowCustom) return;
        const text = event.clipboardData?.getData('text') ?? '';
        if (!text) return;
        event.preventDefault();
        this.query = text;
        this.commitCustom();
    }

    remove(index: number, event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        if (this.disabled) return;
        if (index < 0 || index >= this.value.length) return;
        this.value = this.value.filter((_, i) => i !== index);
        this.onChange([...this.value]);
        this.focusField();
    }

    onKeydown(event: KeyboardEvent): void {
        const visible = this.visibleSuggestions;
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                if (!this.open) this.openPanel();
                this.activeIndex = Math.min(this.activeIndex + 1, visible.length - 1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.activeIndex = Math.max(this.activeIndex - 1, 0);
                break;
            case 'Enter':
                if (this.open && this.activeIndex >= 0 && visible[this.activeIndex]) {
                    event.preventDefault();
                    this.selectSuggestion(visible[this.activeIndex]);
                } else if (this.allowCustom && this.query.trim()) {
                    event.preventDefault();
                    this.commitCustom();
                }
                break;
            case ',':
                if (this.allowCustom && this.query.trim()) {
                    event.preventDefault();
                    this.commitCustom();
                }
                break;
            case 'Escape':
                if (this.open) {
                    event.preventDefault();
                    event.stopPropagation();
                    this.closePanel();
                }
                break;
            case 'Backspace':
                if (!this.query && this.value.length) {
                    this.remove(this.value.length - 1);
                }
                break;
        }
    }

    onBlur(): void {
        this.focused = false;
        this.onTouched();
        // Free-text filters commit a half-typed value on blur, like the previous chips input.
        if (this.allowCustom && this.query.trim()) {
            this.commitCustom();
        }
    }

    @HostListener('document:mousedown', ['$event'])
    onDocumentMousedown(event: MouseEvent): void {
        if (this.open && this.root && !this.root.nativeElement.contains(event.target as Node)) {
            this.closePanel();
        }
    }
}
